import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { ActionMenu, StatusChip, type ActionMenuItem, type StatusPillTone } from '../Common'
import { PathOnlyRow } from '../Common/PathOnlyRow'
import { Pressable } from '../Common/Pressable'
import { useOrbit } from '../Common/Orbit/useOrbit'
import { withLcmChip } from '../Common/Orbit/lcmOrbitSlices'
import { useNls } from '../../nls/nlsProvider'
import { useLcmChipDispatchers } from '../Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from '../Common/Orbit/orbitSliceHelpers'
import { resolveOrbitActionId } from '../Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../../services/actions/registry'
import { buildBoardCanvasSlices, buildBoardCardSlices, buildCellSlices, buildColumnHeaderSlices } from './board.orbit'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { useTimeline } from '../../context/useTimeline'
import type { TimelineElement } from '../../types/timelineElement'
import { useNavigation } from '../../context/useNavigation'
import { useWorkspace } from '../../context/useWorkspace'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import type { BoardStatus, ChecklistItem, ChecklistWorkflowStatus, TimelineWorkflowStatus } from '../../types/schema'
import { createDeltaOperationId, ChecklistBlockerError } from '../../services/stripboardDeltaService'
import { isDropAllowedForLane } from '../../services/checklistStatusMachine'
import { getChecklistScheduleSpan } from '../../services/checklistDurationHelpers'
import { useChecklistDetailPopup } from '../../context/useChecklistDetailPopup'
import type { BoardActionOps } from '../../services/actions/action'
import type { DashboardViewProps } from '../../types/dashboardView'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useResourceReadiness } from '../../hooks/useResourceReadiness'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import { ResourceBadges } from '../Resources/ResourceBadges'
import type { ResourceSignal } from '../../types/resource'

import {
  CHECKLIST_STATUS_LANE,
  CHECKLIST_STATUSES,
  decideCardClickAction,
  decideDropAction,
  LANE_BY_ID,
  LANE_DEFAULT_CHECKLIST_STATUS,
  LANES,
  TIMELINE_STATUSES,
  type StripboardLane,
  type StripboardRecord,
  type StripboardRecordKind,
} from './stripboardDropDecider'

const STRIPBOARD_STATUS_TONES: Record<TimelineWorkflowStatus | ChecklistWorkflowStatus, StatusPillTone> = {
  TODO: 'neutral',
  IN_PROGRESS: 'info',
  COMPLETE: 'success',
  CREATED: 'neutral',
  ASSIGNED: 'neutral',
  ASSESSING: 'neutral',
  ACCEPTED: 'neutral',
  ONGOING: 'info',
  COMPLETED: 'success',
  NOT_REQUIRED: 'neutral',
  POSTPONED: 'neutral',
}

const getStripboardStatusTone = (status: TimelineWorkflowStatus | ChecklistWorkflowStatus | null | undefined): StatusPillTone => (status ? STRIPBOARD_STATUS_TONES[status] : 'neutral')

const recordDate = (record: StripboardRecord) => record.source === 'ELEMENT'
  ? record.element.startDate ?? null
  : getChecklistScheduleSpan(record.checklist)?.startDate ?? null

const recordEndDate = (record: StripboardRecord) => record.source === 'ELEMENT'
  ? record.element.endDate ?? null
  : getChecklistScheduleSpan(record.checklist)?.endDate ?? null

const recordLane = (record: StripboardRecord): StripboardLane => {
  if (record.source === 'ELEMENT') {
    return record.element.boardStatus ?? 'UNSCHEDULED'
  }
  // Checklists have a richer workflow than the element boardStatus tri-state,
  // so the workflowStatus wins when it maps to a dedicated lane (ASSESSING /
  // POSTPONED). Everything else falls back to the shared boardStatus lane.
  const workflow = record.checklist.workflowStatus
  if (workflow) return CHECKLIST_STATUS_LANE[workflow]
  return record.checklist.boardStatus ?? 'UNSCHEDULED'
}

const recordPriority = (record: StripboardRecord) => {
  return record.source === 'ELEMENT' ? record.element.priority : record.checklist.priority
}

const recordTitle = (record: StripboardRecord) => record.source === 'ELEMENT'
  ? record.element.title
  : record.checklist.title

const recordDependencies = (record: StripboardRecord) => record.source === 'ELEMENT'
  ? record.element.dependencyNodeIds ?? []
  : record.checklist.dependencyNodeIds ?? []

const isInNavigationBranch = (
  record: StripboardRecord,
  elementsById: ReadonlyMap<string, TimelineElement>,
  activeGoalId: string | null,
  activeTaskId: string | null,
) => {
  const parentId = record.source === 'ELEMENT' ? record.element.parentId : record.checklist.parentContainerId
  if (!activeGoalId) return true
  if (activeTaskId) return parentId === activeTaskId
  if (parentId === activeGoalId) return true
  const parent = parentId ? elementsById.get(parentId) : undefined
  return parent?.parentId === activeGoalId
}

const compareRecords = (left: StripboardRecord, right: StripboardRecord) => {
  const priorityDifference = recordPriority(right) - recordPriority(left)
  if (priorityDifference !== 0) return priorityDifference
  const leftDue = recordEndDate(left) ?? '9999-12-31'
  const rightDue = recordEndDate(right) ?? '9999-12-31'
  const dueDifference = leftDue.localeCompare(rightDue)
  return dueDifference || recordTitle(left).localeCompare(recordTitle(right))
}

const parseDayMs = (iso: string | null): number | null => {
  if (!iso) return null
  const ms = new Date(iso).getTime()
  return Number.isFinite(ms) ? ms : null
}

const computeDurationDays = (record: StripboardRecord): number | null => {
  const startMs = parseDayMs(recordDate(record))
  if (startMs == null) return null
  const endMs = parseDayMs(recordEndDate(record)) ?? startMs
  const days = Math.round((endMs - startMs) / 86_400_000) + 1
  return Math.max(1, days)
}

const clampPriority = (value: number): 1 | 2 | 3 | 4 | 5 => {
  if (!Number.isFinite(value)) return 3
  const rounded = Math.round(value)
  if (rounded <= 1) return 1
  if (rounded >= 5) return 5
  return rounded as 1 | 2 | 3 | 4 | 5
}

const formatDateLabel = (iso: string | null): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime())) return null
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const getCurrentCoordinates = () => new Promise<{ lat: number; lng: number }>((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Location verification is not available in this browser.'))
    return
  }
  navigator.geolocation.getCurrentPosition(
    ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
    () => reject(new Error('Location access was denied.')),
    { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 },
  )
})

const StripboardCard = ({
  record,
  canMutate,
  isOverlay = false,
  overlayWidth,
  onOpen,
  onStatusChange,
  onOpenDependency,
  resourceSignals = [],
  stripFillRatio,
  durationDays,
}: {
  record: StripboardRecord
  canMutate: boolean
  isOverlay?: boolean
  overlayWidth?: number | null
  onOpen: () => void
  onStatusChange: (status: TimelineWorkflowStatus | ChecklistWorkflowStatus) => void
  onOpenDependency: () => void
  resourceSignals?: ResourceSignal[]
  stripFillRatio: number | null
  durationDays: number | null
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: record.id,
    disabled: !canMutate || isOverlay,
  })
  const { t } = useNls()

  const priorityLevel = clampPriority(recordPriority(record))
  const durationLabel = durationDays != null
    ? `${durationDays} ${durationDays === 1 ? 'day' : 'days'}`
    : 'No schedule'
  const startLabel = formatDateLabel(recordDate(record))
  const endLabel = formatDateLabel(recordEndDate(record))
  const dateRangeLabel = startLabel && endLabel && startLabel !== endLabel
    ? `${startLabel} → ${endLabel}`
    : startLabel ?? 'No dates'

  const style: CSSProperties | undefined = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined
  const item = record.source === 'ELEMENT' ? record.element : record.checklist
  const status = record.source === 'ELEMENT' ? record.element.boardStatus : record.checklist.workflowStatus
  const statusOptions = record.source === 'ELEMENT' ? TIMELINE_STATUSES : CHECKLIST_STATUSES
  const dependencyCount = recordDependencies(record).length
  const kindLabel = record.source === 'ELEMENT' ? record.element.type : 'CHECKLIST'
  const fillPercent = stripFillRatio == null
    ? 0
    : Math.max(6, Math.min(100, Math.round(stripFillRatio * 100)))

  const statusMenuItems: ActionMenuItem[] = statusOptions.map((option, index) => ({
    key: option,
    label: option.replaceAll('_', ' '),
    icon: <span className="stripboardQuickMenuDot" data-option={option} aria-hidden="true" />,
    isActive: status === option,
    groupHeading: index === 0 ? 'Quick status' : undefined,
    onSelect: () => onStatusChange(option),
  }))

  const handleCardKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    // Don't intercept keys aimed at the drag handle / menu toggle.
    if (event.target !== event.currentTarget) return
    event.preventDefault()
    onOpen()
  }

  const handleDependencyClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (dependencyCount > 0) onOpenDependency()
  }

  const articleStyle: CSSProperties | undefined = isOverlay && overlayWidth != null
    ? { ...(style ?? {}), width: overlayWidth }
    : style
  return (
    <article
      ref={setNodeRef}
      style={articleStyle}
      className={`stripboardCard${isDragging ? ' isDragging' : ''}${isOverlay ? ' isOverlay' : ''}${!canMutate ? ' isReadOnly' : ''}`}
      data-priority={priorityLevel}
      data-kind={record.source}
      data-has-schedule={stripFillRatio != null ? 'true' : 'false'}
      data-stripboard-card-id={record.id}
      data-orbit-target="card"
      data-card-id={record.id}
      data-orbit-title={recordTitle(record)}
      role="button"
      tabIndex={isOverlay ? -1 : 0}
      aria-label={`${kindLabel.toLowerCase()} ${recordTitle(record)}, priority ${priorityLevel}, ${durationLabel}`}
      onClick={onOpen}
      onKeyDown={handleCardKey}
    >
      <div className="stripboardCardEdge" aria-hidden="true">
        <span className="stripboardPriorityBadge" aria-hidden="true">P{priorityLevel}</span>
      </div>
      {canMutate && (
        <span
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ActionMenu
            trigger="kebab"
            ariaLabel={`Quick actions for ${recordTitle(record)}`}
            items={statusMenuItems}
          />
        </span>
      )}
      <div className="stripboardCardHeading">
        <Pressable
          className="stripboardDragHandle"
          aria-label={`Move ${recordTitle(record)}`}
          disabled={!canMutate}
          {...attributes}
          tabIndex={-1}
          {...listeners}
          onClick={(event) => event.stopPropagation()}
        >
          <DragIndicatorRoundedIcon fontSize="inherit" />
        </Pressable>
        <div className="stripboardHeadingText">
          <span className="stripboardKind" data-kind={record.source}>{kindLabel}</span>
          <h4>{recordTitle(record)}</h4>
        </div>
      </div>
      <div
        className="stripboardStripRibbon"
        data-empty={stripFillRatio == null ? 'true' : 'false'}
        title={durationLabel}
        aria-hidden="true"
      >
        <span
          className="stripboardStripRibbonFill"
          style={stripFillRatio != null ? { width: `${fillPercent}%` } : undefined}
        />
      </div>
      {'description' in item && item.description && (
        <p className="stripboardDescription">{item.description}</p>
      )}
      <div className="stripboardCardMeta">
        {record.source === 'CHECKLIST' && (record.checklist.checklistType === 'DAILY' || record.checklist.checklistType === 'WEEKLY') && (
          <StatusChip tone="info" size="sm" className="stripboardTodayChip">{t('NLSChecklistOccurrenceToday')}</StatusChip>
        )}
        <StatusChip tone="neutral" size="sm">{durationLabel}</StatusChip>
        <StatusChip tone="neutral" size="sm">{dateRangeLabel}</StatusChip>
      </div>
      <div className="stripboardCardFooter">
        <StatusChip size="sm" tone={getStripboardStatusTone(status)}>
          {status?.replaceAll('_', ' ') ?? 'UNSCHEDULED'}
        </StatusChip>
        <div className="stripboardFooterCluster">
          <ResourceBadges signals={resourceSignals} compact />
          {dependencyCount > 0 && (
            <Pressable
              className="stripboardDependencyChip"
              onClick={handleDependencyClick}
              aria-label={`${dependencyCount} dependenc${dependencyCount === 1 ? 'y' : 'ies'} — open first`}
              title={`${dependencyCount} dependenc${dependencyCount === 1 ? 'y' : 'ies'}`}
            >
              <WarningAmberRoundedIcon fontSize="inherit" />
              <span>{dependencyCount}</span>
            </Pressable>
          )}
        </div>
      </div>
    </article>
  )
}

const StripboardColumn = ({
  lane,
  records,
  isForbidden,
  children,
}: {
  lane: (typeof LANES)[number]
  records: StripboardRecord[]
  isForbidden: boolean
  children: ReactNode
}) => {
  const { isOver, setNodeRef } = useDroppable({ id: lane.id })
  const columnClass = [
    'stripboardLane',
    `stripboardLane--${lane.id.toLowerCase()}`,
    !lane.dropAllowed ? 'stripboardLane--actionOnly' : '',
    isOver ? 'isOver' : '',
    isOver && isForbidden ? 'isForbidden' : '',
  ].filter(Boolean).join(' ')

  return (
    <section
      ref={setNodeRef}
      className={columnClass}
      data-stripboard-lane={lane.id}
      data-orbit-target="cell"
      data-cell-id={lane.id}
      aria-dropeffect={!lane.dropAllowed ? 'none' : undefined}
      aria-label={
        !lane.dropAllowed
          ? `${lane.label} — action needed to move items here, ${records.length} card${records.length === 1 ? '' : 's'}`
          : `${lane.label} lane, ${records.length} card${records.length === 1 ? '' : 's'}`
      }
    >
      <div className="stripboardLaneAccent" aria-hidden="true" />
      <header>
        <div data-orbit-target="column-header" data-column-id={lane.id} data-orbit-title={lane.label} className="stripboardLaneHeading">
          <span className="stripboardLaneDot" aria-hidden="true" />
          <div>
            <h3>
              {lane.label}
              {!lane.dropAllowed && (
                <LockRoundedIcon
                  className="stripboardLaneLockIcon"
                  fontSize="inherit"
                  titleAccess="Action needed to move items here"
                  aria-hidden="true"
                />
              )}
            </h3>
            <p>{lane.hint}</p>
          </div>
        </div>
        <span className="stripboardLaneCount" aria-hidden="true">{records.length}</span>
      </header>
      <div className="stripboardLaneBody">
        {records.length
          ? children
          : (
            <div className="stripboardEmptyLane">
              <span className="stripboardEmptyLanePulse" aria-hidden="true" />
              <span>{lane.dropAllowed ? 'Drop cards here' : 'No items here yet'}</span>
            </div>
          )}
        {isOver && !lane.dropAllowed && (
          <div className="stripboardActionOnlyNotice" role="status">
            <LockRoundedIcon fontSize="inherit" />
            <span>Action needed in details drawer to move items here.</span>
          </div>
        )}
        {isOver && isForbidden && (
          <div className="stripboardForbiddenNotice" role="status">
            <BlockRoundedIcon fontSize="inherit" />
            <span>Checklists only — timeline items reroute automatically.</span>
          </div>
        )}
      </div>
    </section>
  )
}

export const StripboardMatrixView = ({ onElementClick }: DashboardViewProps) => {
  const {
    elements,
    checklistItems,
    applyStripboardDelta,
    isPublicReadOnly,
    createElement,
    deleteElement,
    canPerformOnNode,
    pathOnlyElementIds = new Set(),
  } = useTimeline()
  const { activeGoalId, activeTaskId } = useNavigation()
  const { canPerform, activeWorkspaceId, workspaces } = useWorkspace()
  const { todayIso, weekKey } = useWorkspaceDay(activeWorkspaceId)
  const { user } = useAuth()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null
  const { showToast } = useToast()
  const lcmDispatchers = useLcmChipDispatchers()
  const boardOpsRef = useRef<BoardActionOps>({})
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) { showToast('Coming in Orbit Phase 6'); return }
    const actionId = resolveOrbitActionId(sliceId, 'board')
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: user?.uid ?? '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        boardOps: boardOpsRef.current,
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) { lcmDispatch(id, title); return }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [user?.uid, activeWorkspaceId, canPerform, lcmDispatchers, showToast])
  const orbitDescriptor = useMemo(() => ({
    page: 'board',
    centerLabel: 'Board',
    onCreate: () => boardOpsRef.current?.createCard?.(),
    contextSlices: () => [],
    targets: withLcmChip({
      card: (node: HTMLElement) => buildBoardCardSlices(node, onOrbitAction),
      'column-header': (node: HTMLElement) => buildColumnHeaderSlices(node, onOrbitAction),
      cell: (node: HTMLElement) => buildCellSlices(node, onOrbitAction),
      canvas: () => buildBoardCanvasSlices(onOrbitAction),
    }, onOrbitAction),
  }), [onOrbitAction])
  useOrbit(orbitDescriptor)
  const { openChecklistDetail } = useChecklistDetailPopup()
  const [activeRecordId, setActiveRecordId] = usePersistedState<string | null>('stripboard.activeRecordId', null)

  boardOpsRef.current = {
    openCard: (id: string) => {
      setActiveRecordId(id)
      openChecklistDetail(id)
    },
    createCard: async () => {
      await createElement?.({
        type: 'TASK',
        title: 'New Task',
        parentId: activeGoalId ?? null,
        progress: 0,
        weight: 1,
      })
    },
    createColumn: async () => {
      await createElement?.({
        type: 'GOAL',
        title: 'New Column',
        parentId: null,
        progress: 0,
        weight: 1,
      })
    },
    deleteCard: async (id: string) => {
      const cleanId = id.replace(/^(element:|checklist:)/, '')
      await deleteElement?.(cleanId)
    },
  }
  const [overlayWidth, setOverlayWidth] = useState<number | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const resourceReadiness = useResourceReadiness(activeWorkspace, checklistItems, elements)

  const elementsById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])
  const records = useMemo(() => {
    const elementRecords: StripboardRecord[] = elements
      .filter((element) => element.board === 'DASHBOARD')
      .map((element) => ({ source: 'ELEMENT', id: `element:${element.id}`, element }))
    const checklistRecords: StripboardRecord[] = checklistItems
      .filter((item) => (item.board ?? 'DASHBOARD') === 'DASHBOARD' && item.nodeKind !== 'GATE_AND' && item.nodeKind !== 'GATE_OR')
      .map((checklist) => ({ source: 'CHECKLIST', id: `checklist:${checklist.id}`, checklist }))
    return [...elementRecords, ...checklistRecords]
      .filter((record) => isInNavigationBranch(record, elementsById, activeGoalId, activeTaskId))
  }, [activeGoalId, activeTaskId, checklistItems, elements, elementsById])

  const recordsByDragId = useMemo(() => new Map(records.map((record) => [record.id, record])), [records])
  const recordsByLane = useMemo(() => new Map(LANES.map((lane) => [
    lane.id,
    records.filter((record) => recordLane(record) === lane.id).sort(compareRecords),
  ])), [records])

  const durationsByRecord = useMemo(() => {
    const map = new Map<string, number | null>()
    for (const record of records) map.set(record.id, computeDurationDays(record))
    return map
  }, [records])

  const maxDurationDays = useMemo(() => {
    let max = 0
    for (const value of durationsByRecord.values()) {
      if (value != null && value > max) max = value
    }
    return max
  }, [durationsByRecord])

  const canMutateRecord = (record: StripboardRecord) => {
    if (isPublicReadOnly) return false
    return record.source === 'ELEMENT'
      ? canPerformOnNode('timeline.edit', record.element.id)
      : canPerformOnNode('checklist.change_status', record.checklist.id)
  }

  const moveRecord = async (record: StripboardRecord, lane: StripboardLane, checklistWorkflowStatus?: ChecklistWorkflowStatus) => {
    if (!canMutateRecord(record)) return
    if (record.source === 'CHECKLIST' && !isDropAllowedForLane(lane)) {
      throw new Error(`Cannot directly drop into action-only lane ${lane}`)
    }
    const laneMeta = LANE_BY_ID[lane]
    // Elements have only a tri-state boardStatus; silently ignore drops onto
    // checklist-only workflow lanes (ASSESSING / POSTPONED).
    if (record.source === 'ELEMENT' && !laneMeta.allowedKinds.includes('ELEMENT')) return
    if (recordLane(record) === lane && (record.source === 'ELEMENT' || !checklistWorkflowStatus || record.checklist.workflowStatus === checklistWorkflowStatus)) return
    const resolvedChecklistStatus = checklistWorkflowStatus ?? LANE_DEFAULT_CHECKLIST_STATUS[lane]
    try {
      const item = record.source === 'ELEMENT' ? record.element : record.checklist
      const destinationStatus: BoardStatus | null = laneMeta.elementBoardStatus
      const coordinates = record.source === 'CHECKLIST'
        && lane === 'COMPLETE'
        && (resolvedChecklistStatus ?? 'COMPLETED') === 'COMPLETED'
        && record.checklist.requiresLocationPin
        ? await getCurrentCoordinates()
        : null
      const isRepeatingChecklist = record.source === 'CHECKLIST' && (record.checklist.checklistType === 'DAILY' || record.checklist.checklistType === 'WEEKLY')
      const periodKey = isRepeatingChecklist
        ? (record.checklist.checklistType === 'WEEKLY' ? weekKey : todayIso)
        : null
      const result = await applyStripboardDelta({
        operationId: createDeltaOperationId('stripboard-status', item.id),
        operationType: 'STATUS_MOVE',
        recordId: item.id,
        recordKind: record.source === 'ELEMENT' ? record.element.type : 'CHECKLIST',
        sourceStatus: item.boardStatus,
        destinationStatus,
        expectedVersion: item.recordVersion,
        ...(record.source === 'CHECKLIST' ? {
          checklistWorkflowStatus: resolvedChecklistStatus,
          coordinates,
          periodKey,
        } : {}),
      })
      if (result.dependencyAlertNodeIds.length) {
        const blockerId = result.dependencyAlertNodeIds[0]
        showToast(
          `Dependency Alert: ${result.dependencyAlertNodeIds.length} prerequisite${result.dependencyAlertNodeIds.length === 1 ? ' is' : 's are'} incomplete.`,
          'warning',
          {
            label: 'View Dependencies',
            run: () => {
              const element = elements.find((candidate) => candidate.id === blockerId)
              if (element) return onElementClick(element)
              const checklist = checklistItems.find((candidate) => candidate.id === blockerId)
              if (checklist) openRecord({ source: 'CHECKLIST', id: `checklist:${checklist.id}`, checklist })
            },
          },
        )
      }
    } catch (error) {
      if (error instanceof ChecklistBlockerError) {
        const firstBlocker = error.blockers[0]
        const message = firstBlocker?.message ?? error.message
        showToast(
          message,
          'error',
          record.source === 'CHECKLIST' && resolvedChecklistStatus
            ? {
                label: 'Open checklist',
                run: () => openChecklistDetail(record.checklist.id, { pendingStatus: resolvedChecklistStatus }),
              }
            : undefined,
        )
        return
      }
      showToast(error instanceof Error ? error.message : 'Could not move this card.', 'error')
    }
  }

  const handleStatusChange = (record: StripboardRecord, status: TimelineWorkflowStatus | ChecklistWorkflowStatus) => {
    if (record.source === 'ELEMENT') {
      void moveRecord(record, status as TimelineWorkflowStatus)
      return
    }
    const checklistStatus = status as ChecklistWorkflowStatus
    if (checklistStatus === 'COMPLETED') {
      void moveRecord(record, 'COMPLETE', checklistStatus)
      return
    }
    void moveRecord(record, CHECKLIST_STATUS_LANE[checklistStatus], checklistStatus)
  }

  const openRecord = (record: StripboardRecord, displayedLane?: StripboardLane) => {
    const decision = decideCardClickAction(record, displayedLane)
    if (decision.action === 'ELEMENT') {
      onElementClick(decision.element)
      return
    }
    openChecklistDetail(decision.checklistId, decision.pendingStatus ? { pendingStatus: decision.pendingStatus } : undefined)
  }

  const openDependencyFor = (record: StripboardRecord) => {
    const deps = recordDependencies(record)
    const firstId = deps[0]
    if (!firstId) return
    const element = elements.find((candidate) => candidate.id === firstId)
    if (element) {
      onElementClick(element)
      return
    }
    const checklist = checklistItems.find((candidate) => candidate.id === firstId)
    if (checklist) openChecklistDetail(checklist.id)
  }

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveRecordId(String(active.id))
    // Measure the actual source card so the drag ghost matches its rendered
    // width — otherwise a fixed-width overlay looks oversized on narrow lanes
    // and undersized on wide viewports.
    const id = String(active.id)
    const source = typeof document !== 'undefined'
      ? document.querySelector<HTMLElement>(`[data-stripboard-card-id="${CSS.escape(id)}"]`)
      : null
    setOverlayWidth(source ? source.getBoundingClientRect().width : null)
  }
  const clearDragState = () => {
    setActiveRecordId(null)
    setOverlayWidth(null)
  }
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const record = recordsByDragId.get(String(active.id))
    clearDragState()
    if (!record || !over) return
    const laneMeta = LANES.find((l) => l.id === String(over.id))
    const decision = decideDropAction(record, laneMeta)
    if (decision.action === 'OPEN_DRAWER') {
      openChecklistDetail(decision.checklistId, { pendingStatus: decision.pendingStatus })
      return
    }
    if (decision.action === 'MOVE_RECORD') {
      void moveRecord(decision.record, decision.lane, decision.checklistWorkflowStatus)
    }
  }

  const activeRecord = activeRecordId ? recordsByDragId.get(activeRecordId) : undefined
  const activeKind: StripboardRecordKind | null = activeRecord?.source ?? null
  const stripFillFor = (record: StripboardRecord): number | null => {
    const duration = durationsByRecord.get(record.id) ?? null
    if (duration == null || maxDurationDays <= 0) return null
    return duration / maxDurationDays
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragCancel={clearDragState} onDragEnd={handleDragEnd}>
      <div data-orbit-target="canvas" className="stripboardViewport" data-dashboard-horizontal-scroll="true">
        <div className="stripboardBoard">
          {LANES.map((lane) => {
            const laneRecords = recordsByLane.get(lane.id) ?? []
            const isForbidden = activeKind != null && !lane.allowedKinds.includes(activeKind)
            return (
              <StripboardColumn key={lane.id} lane={lane} records={laneRecords} isForbidden={isForbidden}>
                {laneRecords.map((record) => {
                  if (record.source === 'ELEMENT' && pathOnlyElementIds.has(record.element.id)) {
                    return (
                      <PathOnlyRow
                        key={record.id}
                        title={record.element.title}
                        variant="stripboard"
                      />
                    )
                  }
                  return (
                    <StripboardCard
                      key={record.id}
                      record={record}
                      canMutate={canMutateRecord(record)}
                      onOpen={() => openRecord(record, lane.id)}
                      onStatusChange={(status) => handleStatusChange(record, status)}
                      onOpenDependency={() => openDependencyFor(record)}
                      resourceSignals={resourceReadiness.getSignalsForNode(
                        (record.source === 'CHECKLIST' ? record.checklist : record.element) as ChecklistItem,
                      ).signals}
                      stripFillRatio={stripFillFor(record)}
                      durationDays={durationsByRecord.get(record.id) ?? null}
                    />
                  )
                })}
              </StripboardColumn>
            )
          })}
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 160, easing: 'ease-out' }}>
        {activeRecord && (
          <StripboardCard
            record={activeRecord}
            canMutate={false}
            isOverlay
            overlayWidth={overlayWidth}
            onOpen={() => undefined}
            onStatusChange={() => undefined}
            onOpenDependency={() => undefined}
            resourceSignals={activeRecord.source === 'CHECKLIST' ? resourceReadiness.getSignalsForNode(activeRecord.checklist).signals : []}
            stripFillRatio={stripFillFor(activeRecord)}
            durationDays={durationsByRecord.get(activeRecord.id) ?? null}
          />
        )}
      </DragOverlay>
    </DndContext>
  )
}
